import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Columns,
  TableProperties,
  AlertTriangle,
  FolderOpen,
  Trash2,
  Paperclip,
  ExternalLink,
  LayoutGrid,
  Pencil,
  X,
  FileText,
  Film,
  Image,
  Layers,
  Video,
  BookOpen,
  BarChart3,
  Share2,
  Link2,
  Copy,
  Check,
  Loader2,
  Unlink,
  Download,
  Lightbulb,
  Hammer,
  Eye,
  CheckCircle2,
  Send,
  CalendarDays,
} from 'lucide-react'
import { pedApi, projectsApi, socialApi, getApiErrorMessage } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { useProjectFilter, ProjectFilterBanner } from '@/components/common/ProjectFilterBanner'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/common/Badge'
import { StatCard } from '@/components/common/StatCard'
import { PEDCalendar } from '@/components/ped/PEDCalendar'
import { ContentCard, PLATFORM_COLORS } from '@/components/ped/ContentCard'
import { ContentDetailModal } from '@/components/ped/ContentDetailModal'
import { ContentFormModal, type ContentFormData } from '@/components/ped/ContentFormModal'
import { GuestPedPage } from './GuestPedPage'
import type { Content, ContentStatus, ContentType, Platform, PaginatedResponse, SocialFormat } from '@/types'

// ─── Kanban columns for PED ───────────────────────────────────────────────────

interface PEDKanbanColumn {
  id: ContentStatus
  title: string
  dotColor: string
  borderColor: string
}

const KANBAN_COLUMNS: PEDKanbanColumn[] = [
  { id: 'IDEA', title: 'Crea', dotColor: 'bg-slate-400', borderColor: 'border-slate-200' },
  { id: 'IN_PRODUCTION', title: 'Produci', dotColor: 'bg-warning-400', borderColor: 'border-warning-100' },
  { id: 'IN_REVIEW', title: 'Approva', dotColor: 'bg-warning-500', borderColor: 'border-warning-100' },
  { id: 'SCHEDULED', title: 'Pubblica', dotColor: 'bg-primary-400', borderColor: 'border-primary-200' },
  { id: 'PUBLISHED', title: 'Pubblicato', dotColor: 'bg-success-500', borderColor: 'border-success-200' },
]

const PLATFORM_FILTER_OPTIONS: { value: Platform | ''; label: string }[] = [
  { value: '', label: 'Tutte' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'PINTEREST', label: 'Pinterest' },
]

const MONTH_LABELS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

// ─── Kanban board ─────────────────────────────────────────────────────────────

function PEDKanban({
  contents,
  onContentClick,
  onContentMove,
  onQuickAdd,
}: {
  contents: Content[]
  onContentClick: (c: Content) => void
  onContentMove?: (contentId: string, newStatus: ContentStatus) => void
  onQuickAdd?: (status: ContentStatus) => void
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverCol, setDragOverCol] = useState<ContentStatus | null>(null)

  const byStatus = KANBAN_COLUMNS.reduce<Record<ContentStatus, Content[]>>(
    (acc, col) => {
      acc[col.id] = contents.filter((c) => STATUS_TO_STEP_STATUS[c.status] === col.id)
      return acc
    },
    {} as Record<ContentStatus, Content[]>
  )

  function handleDragStart(e: React.DragEvent, id: string) {
    e.dataTransfer.setData('contentId', id)
    setDraggingId(id)
  }

  function handleDrop(e: React.DragEvent, colId: ContentStatus) {
    e.preventDefault()
    const id = e.dataTransfer.getData('contentId')
    if (id && onContentMove) onContentMove(id, colId)
    setDraggingId(null)
    setDragOverCol(null)
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {KANBAN_COLUMNS.map((col) => {
        const colContents = byStatus[col.id] ?? []
        const isOver = dragOverCol === col.id

        return (
          <div
            key={col.id}
            className={cn(
              'flex flex-col min-w-[220px] max-w-[240px] flex-shrink-0',
              'bg-slate-50 rounded-2xl border border-slate-200 transition-colors',
              isOver && 'bg-primary-50 border-primary-300'
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id) }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
              <div className={cn('w-2 h-2 rounded-full flex-shrink-0', col.dotColor)} />
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex-1">
                {col.title}
              </span>
              <Badge variant="neutral" size="sm">{colContents.length}</Badge>
            </div>
            <div className="flex-1 p-2 space-y-2 min-h-[160px]">
              {colContents.map((c) => (
                <div
                  key={c.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, c.id)}
                  onDragEnd={() => { setDraggingId(null); setDragOverCol(null) }}
                >
                  <ContentCard
                    content={c}
                    onClick={() => onContentClick(c)}
                    dragging={draggingId === c.id}
                  />
                </div>
              ))}
              {colContents.length === 0 && (
                <div className={cn(
                  'h-full min-h-[60px] flex items-center justify-center',
                  'border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-400',
                  isOver && 'border-primary-300 bg-primary-50/50'
                )}>
                  {isOver ? 'Rilascia qui' : 'Nessun contenuto'}
                </div>
              )}
              {onQuickAdd && (
                <button
                  type="button"
                  onClick={() => onQuickAdd(col.id)}
                  className={cn(
                    'w-full mt-1 py-1.5 flex items-center justify-center gap-1',
                    'text-xs text-slate-400 rounded-lg border border-dashed border-slate-200',
                    'hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50/50 transition-colors'
                  )}
                >
                  <Plus className="w-3 h-3" />
                  Aggiungi
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Table View constants ────────────────────────────────────────────────────

const TABLE_TYPE_OPTIONS: { value: ContentType; label: string }[] = [
  { value: 'POST', label: 'Post' },
  { value: 'REEL', label: 'Reel' },
  { value: 'STORY', label: 'Story' },
  { value: 'CAROUSEL', label: 'Carosello' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'ARTICLE', label: 'Articolo' },
  { value: 'INFOGRAPHIC', label: 'Infografica' },
]

const TABLE_PLATFORM_OPTIONS: { value: Platform; label: string }[] = [
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'TWITTER', label: 'Twitter / X' },
  { value: 'PINTEREST', label: 'Pinterest' },
]

const TABLE_STATUS_OPTIONS: { value: ContentStatus; label: string }[] = [
  { value: 'IDEA', label: 'Crea' },
  { value: 'IN_PRODUCTION', label: 'Produci' },
  { value: 'IN_REVIEW', label: 'Approva' },
  { value: 'SCHEDULED', label: 'Pubblica' },
  { value: 'PUBLISHED', label: 'Pubblicato' },
]

const STATUS_DOT_COLOR: Record<ContentStatus, string> = {
  IDEA: 'bg-slate-400',
  TODO: 'bg-slate-400',
  IN_PRODUCTION: 'bg-warning-400',
  READY: 'bg-warning-400',
  IN_REVIEW: 'bg-warning-500',
  CHANGES_REQUESTED: 'bg-warning-500',
  APPROVED: 'bg-primary-400',
  CLIENT_APPROVED: 'bg-success-500',
  SCHEDULED: 'bg-primary-400',
  PUBLISHED: 'bg-success-500',
}

// Map sub-statuses to their workflow step status for display
const STATUS_TO_STEP_STATUS: Record<ContentStatus, ContentStatus> = {
  IDEA: 'IDEA',
  TODO: 'IDEA',
  IN_PRODUCTION: 'IN_PRODUCTION',
  READY: 'IN_PRODUCTION',
  IN_REVIEW: 'IN_REVIEW',
  CHANGES_REQUESTED: 'IN_REVIEW',
  APPROVED: 'SCHEDULED',
  CLIENT_APPROVED: 'SCHEDULED',
  SCHEDULED: 'SCHEDULED',
  PUBLISHED: 'PUBLISHED',
}

// ─── Table View ──────────────────────────────────────────────────────────────

interface NewRowData {
  scheduledAt: string
  title: string
  platforms: Platform[]
  type: ContentType
  status: ContentStatus
  caption: string
  hashtags: string
}

function PEDTableView({
  contents,
  projectId,
  onContentClick,
  userLevel,
  canDelete,
}: {
  contents: Content[]
  projectId: string
  onContentClick?: (c: Content) => void
  userLevel: number
  canDelete: boolean
}) {
  const queryClient = useQueryClient()

  // Fetch formats for dropdown
  const { data: fmtData } = useQuery({
    queryKey: ['social-formats', projectId],
    queryFn: async () => { const res = await socialApi.listFormats(projectId); return res.data },
    staleTime: 60_000,
  })
  const tableFormats = ((fmtData as any)?.data ?? fmtData ?? []) as SocialFormat[]

  // Editing state: which cell is being edited
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showPlatformPicker, setShowPlatformPicker] = useState(false)
  const platformBtnRef = useRef<HTMLButtonElement>(null)
  // Multi-platform selection for existing rows
  const [editPlatforms, setEditPlatforms] = useState<Platform[]>([])


  // New row state
  const getDefaultNewRow = (): NewRowData => {
    const now = new Date()
    const yyyy = now.getFullYear()
    const mm = String(now.getMonth() + 1).padStart(2, '0')
    const dd = String(now.getDate()).padStart(2, '0')

    let lastPlatforms: Platform[] = ['INSTAGRAM']
    try {
      const saved = localStorage.getItem('ped-last-platforms')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) lastPlatforms = parsed
      }
    } catch { /* ignore */ }

    const lastType = (localStorage.getItem('ped-last-type') as ContentType) || 'POST'

    return {
      scheduledAt: `${yyyy}-${mm}-${dd}T10:00`,
      title: '',
      platforms: lastPlatforms,
      type: lastType,
      status: 'IDEA',
      caption: '',
      hashtags: '',
    }
  }

  const [newRow, setNewRow] = useState<NewRowData>(getDefaultNewRow)
  const [isCreating, setIsCreating] = useState(false)

  // Sorted contents: by scheduledAt ascending
  const sorted = [...contents].sort((a, b) => {
    const da = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0
    const db = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0
    return da - db
  })

  // ── Inline edit helpers ──

  function startEdit(id: string, field: string, currentValue: string, anchorEl?: HTMLElement) {
    setEditingCell({ id, field })
    setEditValue(currentValue)
    setPopoverRect(anchorEl ? anchorEl.getBoundingClientRect() : null)
  }

  function closeEdit() {
    setEditingCell(null)
    setPopoverRect(null)
  }

  async function saveEdit(id: string, field: string) {
    closeEdit()
    setSavingId(id)
    try {
      let payload: Record<string, unknown> = {}
      if (field === 'scheduledAt') {
        payload = { scheduledAt: editValue ? new Date(editValue).toISOString() : null }
      } else if (field === 'hashtags') {
        payload = { hashtags: editValue.trim() || null }
      } else {
        payload = { [field]: editValue.trim() || null }
      }
      await pedApi.updateContent(id, payload)
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
    } catch { /* silent – data reverts on next refetch */ }
    setSavingId(null)
  }

  async function saveSelectEdit(id: string, field: string, value: string) {
    closeEdit()
    setSavingId(id)
    try {
      await pedApi.updateContent(id, { [field]: value })
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
    } catch { /* silent */ }
    setSavingId(null)
  }

  // ── Save multi-platform selection (updates platforms array on existing content) ──

  async function savePlatformsEdit(id: string) {
    const selected = [...editPlatforms]
    closeEdit()
    if (selected.length === 0) return
    setSavingId(id)
    try {
      await pedApi.updateContent(id, { platforms: selected })
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
    } catch { /* silent */ }
    setSavingId(null)
  }

  // ── Delete ──

  async function handleDelete(id: string) {
    if (!window.confirm('Eliminare questo contenuto?')) return
    setDeletingId(id)
    try {
      await pedApi.deleteContent(id)
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
    } catch (err) {
      const msg = getApiErrorMessage(err)
      window.alert(msg)
    }
    setDeletingId(null)
  }

  // ── Create new row ──

  async function handleCreateRow() {
    if (!newRow.title.trim() || newRow.platforms.length === 0) return
    setIsCreating(true)
    try {
      const hashtagList = newRow.hashtags
        .split(',')
        .map((h) => h.trim().replace(/^#/, ''))
        .filter(Boolean)

      const basePayload = {
        title: newRow.title.trim(),
        type: newRow.type,
        status: newRow.status,
        scheduledAt: newRow.scheduledAt ? new Date(newRow.scheduledAt).toISOString() : undefined,
        caption: newRow.caption.trim() || undefined,
        hashtags: hashtagList.length > 0 ? hashtagList.join(', ') : undefined,
      }

      // Create a single content with all selected platforms
      await pedApi.createContentForProject(projectId, { ...basePayload, platforms: newRow.platforms })

      // Remember last used values
      localStorage.setItem('ped-last-type', newRow.type)
      localStorage.setItem('ped-last-platforms', JSON.stringify(newRow.platforms))

      void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
      setNewRow(getDefaultNewRow())
    } catch { /* silent */ }
    setIsCreating(false)
  }

  // ── Formatters ──

  function formatDate(d?: string) {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return d }
  }

  function toDatetimeLocal(d?: string) {
    if (!d) return ''
    try {
      const dt = new Date(d)
      const yyyy = dt.getFullYear()
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')
      const hh = String(dt.getHours()).padStart(2, '0')
      const min = String(dt.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`
    } catch { return '' }
  }

  // ── Shared cell classes ──

  const cellBase = 'px-3 py-2 text-sm text-slate-700 border-b border-slate-200'
  const inputCellClass = cn(
    'w-full bg-transparent text-sm text-slate-700 border border-primary-300 rounded px-2 py-1',
    'focus:outline-none focus:ring-1 focus:ring-primary-400'
  )

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto">
      <table className="w-full min-w-[900px]">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {['Data', 'Format', 'Titolo', 'Piattaforma', 'Tipo', 'Status', 'Caption', 'Hashtag', ''].map((h) => (
              <th key={h} className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const isSaving = savingId === c.id
            const isDeleting = deletingId === c.id
            const busyOpacity = isSaving || isDeleting ? 'opacity-50' : ''

            // ── Temporal status indicators ──
            const now = new Date()
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const tomorrowStart = new Date(todayStart.getTime() + 86400000)
            const scheduled = c.scheduledAt ? new Date(c.scheduledAt) : null
            const isPublished = c.status === 'PUBLISHED'
            const isReadyToPublish = c.status === 'APPROVED' || c.status === 'SCHEDULED'
            const isOverdue = !!(scheduled && scheduled < now && !isPublished && !isReadyToPublish)
            const isToday = !!(scheduled && scheduled >= todayStart && scheduled < tomorrowStart)

            // Row classes
            const rowClasses = cn(
              'transition-colors',
              busyOpacity,
              isPublished
                ? 'bg-success-50/50 hover:bg-success-50/80'
                : isOverdue
                  ? 'bg-danger-50/40 hover:bg-danger-50/60'
                  : 'hover:bg-slate-50'
            )

            // Left border indicator via first td
            const borderIndicator = isPublished
              ? 'border-l-2 border-l-success-500'
              : isOverdue
                ? 'border-l-2 border-l-danger-500'
                : isReadyToPublish
                  ? 'border-l-2 border-l-success-500'
                  : isToday
                    ? 'border-l-2 border-l-primary-500'
                    : ''

            return (
              <tr key={c.id} className={rowClasses}>
                {/* Data */}
                <td className={cn(cellBase, 'w-[160px] whitespace-nowrap tabular-nums', borderIndicator)}>
                  {editingCell?.id === c.id && editingCell.field === 'scheduledAt' ? (
                    <input
                      type="datetime-local"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(c.id, 'scheduledAt')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(c.id, 'scheduledAt') }}
                      className={inputCellClass}
                      autoFocus
                    />
                  ) : (
                    <span
                      className={cn(
                        'cursor-pointer hover:text-primary-600',
                        isOverdue && 'text-danger-600 font-semibold',
                        isToday && !isOverdue && 'text-primary-600 font-semibold'
                      )}
                      onClick={() => startEdit(c.id, 'scheduledAt', toDatetimeLocal(c.scheduledAt))}
                    >
                      {formatDate(c.scheduledAt)}
                      {isOverdue && <span className="ml-1 text-[10px]" title="In ritardo">!!</span>}
                    </span>
                  )}
                </td>

                {/* Format (dropdown — setta socialFormatId + type + platforms) */}
                <td className={cn(cellBase, 'min-w-[140px]')}>
                  {editingCell?.id === c.id && editingCell.field === 'format' ? (
                    <select
                      value={editValue}
                      onChange={(e) => {
                        const fmtId = e.target.value
                        const fmt = tableFormats.find((f) => f.id === fmtId)
                        setEditValue(fmtId)
                        ;(async () => {
                          closeEdit()
                          setSavingId(c.id)
                          try {
                            await pedApi.updateContent(c.id, {
                              socialFormatId: fmt ? fmt.id : null,
                              ...(fmt ? { type: fmt.type } : {}),
                              ...(fmt && fmt.platforms.length > 0 ? { platforms: fmt.platforms } : {}),
                            })
                            void queryClient.invalidateQueries({ queryKey: ['ped-contents', projectId] })
                          } catch { /* silent */ }
                          setSavingId(null)
                        })()
                      }}
                      className={inputCellClass}
                      autoFocus
                      onBlur={() => setTimeout(() => closeEdit(), 150)}
                    >
                      <option value="">Seleziona...</option>
                      {tableFormats.filter((f) => f.isActive).map((fmt) => (
                        <option key={fmt.id} value={fmt.id}>{fmt.name}</option>
                      ))}
                      {tableFormats.length === 0 && <option disabled>Nessun format — creane uno dalla tab Format</option>}
                    </select>
                  ) : (
                    <span
                      className="cursor-pointer hover:text-primary-600 text-sm"
                      onClick={() => {
                        const matched = tableFormats.find((f) => f.id === c.socialFormatId)
                        startEdit(c.id, 'format', matched?.id ?? '')
                      }}
                    >
                      {tableFormats.find((f) => f.id === c.socialFormatId)?.name || '—'}
                    </span>
                  )}
                </td>

                {/* Titolo (testo libero) */}
                <td className={cn(cellBase, 'min-w-[180px] font-medium')}>
                  {editingCell?.id === c.id && editingCell.field === 'title' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(c.id, 'title')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(c.id, 'title') }}
                      className={inputCellClass}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="cursor-pointer hover:text-primary-600 inline-flex items-center gap-1.5"
                      onClick={() => startEdit(c.id, 'title', c.title ?? '')}
                    >
                      {c.title || '—'}
                      {(c.assets?.length ?? 0) > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-slate-400" title={`${c.assets!.length} allegat${c.assets!.length === 1 ? 'o' : 'i'}`}>
                          <Paperclip className="w-3 h-3" />
                          <span className="text-[10px] font-medium">{c.assets!.length}</span>
                        </span>
                      )}
                    </span>
                  )}
                </td>

                {/* Piattaforma (multi-select) */}
                <td className={cn(cellBase, 'w-[150px]')}>
                  <span
                    className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 flex-wrap"
                    onClick={(e) => {
                      if (editingCell?.id === c.id && editingCell.field === 'platform') {
                        closeEdit()
                      } else {
                        setEditPlatforms([...(c.platforms ?? [])])
                        startEdit(c.id, 'platform', '', e.currentTarget)
                      }
                    }}
                  >
                    {(c.platforms ?? []).map((p) => (
                      <span key={p} className="inline-flex items-center gap-1">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: PLATFORM_COLORS[p] ?? '#94a3b8' }}
                        />
                        <span className="text-xs font-medium">
                          {TABLE_PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p}
                        </span>
                      </span>
                    ))}
                    {(!c.platforms || c.platforms.length === 0) && (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </span>
                  {editingCell?.id === c.id && editingCell.field === 'platform' && popoverRect && createPortal(
                    <>
                      <div className="fixed inset-0 z-[9998]" onClick={() => {
                        void savePlatformsEdit(c.id)
                      }} />
                      <div
                        className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-dropdown p-1.5 min-w-[160px]"
                        style={(() => {
                          const spaceBelow = window.innerHeight - popoverRect.bottom
                          if (spaceBelow > 280) return { left: popoverRect.left, top: popoverRect.bottom + 4 }
                          return { left: popoverRect.left, bottom: window.innerHeight - popoverRect.top + 4 }
                        })()}
                      >
                        {TABLE_PLATFORM_OPTIONS.map((o) => {
                          const isActive = editPlatforms.includes(o.value as Platform)
                          const color = PLATFORM_COLORS[o.value] ?? '#64748B'
                          return (
                            <button
                              key={o.value}
                              type="button"
                              onClick={() => {
                                setEditPlatforms((prev) =>
                                  prev.includes(o.value as Platform)
                                    ? prev.filter((p) => p !== o.value)
                                    : [...prev, o.value as Platform]
                                )
                              }}
                              className={cn(
                                'w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                                isActive ? 'bg-slate-50 text-slate-800' : 'text-slate-500 hover:bg-slate-50'
                              )}
                            >
                              <span
                                className={cn(
                                  'w-3 h-3 rounded border-2 flex-shrink-0 transition-colors',
                                  isActive ? 'border-transparent' : 'border-slate-300'
                                )}
                                style={isActive ? { backgroundColor: color } : undefined}
                              />
                              {o.label}
                            </button>
                          )
                        })}
                        <div className="border-t border-slate-100 mt-1 pt-1">
                          <button
                            type="button"
                            onClick={() => void savePlatformsEdit(c.id)}
                            disabled={editPlatforms.length === 0}
                            className="w-full px-2.5 py-1.5 rounded text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-40"
                          >
                            Conferma{editPlatforms.length > 0 ? ` (${editPlatforms.length})` : ''}
                          </button>
                        </div>
                      </div>
                    </>,
                    document.body
                  )}
                </td>

                {/* Tipo */}
                <td className={cn(cellBase, 'w-[110px]')}>
                  <span
                    className="cursor-pointer text-xs hover:text-primary-600"
                    onClick={(e) => {
                      if (editingCell?.id === c.id && editingCell.field === 'type') {
                        closeEdit()
                      } else {
                        startEdit(c.id, 'type', c.type, e.currentTarget)
                      }
                    }}
                  >
                    {TABLE_TYPE_OPTIONS.find((o) => o.value === c.type)?.label ?? c.type}
                  </span>
                  {editingCell?.id === c.id && editingCell.field === 'type' && popoverRect && createPortal(
                    <>
                      <div className="fixed inset-0 z-[9998]" onClick={() => closeEdit()} />
                      <div
                        className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-dropdown p-1 min-w-[130px]"
                        style={(() => {
                          const spaceBelow = window.innerHeight - popoverRect.bottom
                          if (spaceBelow > 200) return { left: popoverRect.left, top: popoverRect.bottom + 4 }
                          return { left: popoverRect.left, bottom: window.innerHeight - popoverRect.top + 4 }
                        })()}
                      >
                        {TABLE_TYPE_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => { saveSelectEdit(c.id, 'type', o.value) }}
                            className={cn(
                              'w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                              c.type === o.value ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50'
                            )}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </>,
                    document.body
                  )}
                </td>

                {/* Status */}
                <td className={cn(cellBase, 'w-[130px]')}>
                  <span
                    className="inline-flex items-center gap-1.5 cursor-pointer hover:opacity-80"
                    onClick={(e) => {
                      if (editingCell?.id === c.id && editingCell.field === 'status') {
                        closeEdit()
                      } else {
                        startEdit(c.id, 'status', c.status, e.currentTarget)
                      }
                    }}
                  >
                    <span className={cn('w-2 h-2 rounded-full flex-shrink-0', STATUS_DOT_COLOR[c.status])} />
                    <span className="text-xs font-medium">
                      {TABLE_STATUS_OPTIONS.find((o) => o.value === STATUS_TO_STEP_STATUS[c.status])?.label ?? c.status}
                    </span>
                  </span>
                  {editingCell?.id === c.id && editingCell.field === 'status' && popoverRect && createPortal(
                    <>
                      <div className="fixed inset-0 z-[9998]" onClick={() => closeEdit()} />
                      <div
                        className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-dropdown p-1 min-w-[140px]"
                        style={(() => {
                          const spaceBelow = window.innerHeight - popoverRect.bottom
                          if (spaceBelow > 280) return { left: popoverRect.left, top: popoverRect.bottom + 4 }
                          return { left: popoverRect.left, bottom: window.innerHeight - popoverRect.top + 4 }
                        })()}
                      >
                        {TABLE_STATUS_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => { saveSelectEdit(c.id, 'status', o.value) }}
                            className={cn(
                              'w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                              c.status === o.value ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50'
                            )}
                          >
                            <span className={cn('w-2 h-2 rounded-full flex-shrink-0', STATUS_DOT_COLOR[o.value])} />
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </>,
                    document.body
                  )}
                </td>

                {/* Caption */}
                <td className={cn(cellBase, 'max-w-[200px]')}>
                  {editingCell?.id === c.id && editingCell.field === 'caption' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(c.id, 'caption')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(c.id, 'caption') }}
                      className={inputCellClass}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="cursor-pointer text-xs text-slate-500 hover:text-primary-600 truncate block"
                      onClick={() => startEdit(c.id, 'caption', c.caption ?? '')}
                      title={c.caption ?? ''}
                    >
                      {c.caption || '—'}
                    </span>
                  )}
                </td>

                {/* Hashtag */}
                <td className={cn(cellBase, 'max-w-[160px]')}>
                  {editingCell?.id === c.id && editingCell.field === 'hashtags' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(c.id, 'hashtags')}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(c.id, 'hashtags') }}
                      className={inputCellClass}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="cursor-pointer text-xs text-slate-500 hover:text-primary-600 truncate block"
                      onClick={() => startEdit(c.id, 'hashtags', Array.isArray(c.hashtags) ? c.hashtags.join(', ') : (c.hashtags ?? ''))}
                      title={Array.isArray(c.hashtags) ? c.hashtags.join(', ') : (c.hashtags ?? '')}
                    >
                      {(Array.isArray(c.hashtags) ? c.hashtags.join(', ') : c.hashtags) || '—'}
                    </span>
                  )}
                </td>

                {/* Azioni */}
                <td className={cn(cellBase, 'w-[80px]')}>
                  <div className="flex items-center justify-center gap-1">
                    {onContentClick && (
                      <button
                        type="button"
                        onClick={() => onContentClick(c)}
                        className="p-1 rounded text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        title="Apri dettaglio"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        disabled={isDeleting}
                        className="p-1 rounded text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-colors disabled:opacity-30"
                        title="Elimina"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}

          {/* ── New row — hidden for L4 ── */}
          {userLevel < 4 && <tr className={cn('bg-slate-50/70', isCreating && 'opacity-50')}>
            <td className={cn(cellBase)}>
              <input
                type="datetime-local"
                value={newRow.scheduledAt}
                onChange={(e) => setNewRow((r) => ({ ...r, scheduledAt: e.target.value }))}
                className="w-full bg-white text-sm text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                disabled={isCreating}
              />
            </td>
            <td className={cn(cellBase)}>
              <select
                onChange={(e) => {
                  const fmt = tableFormats.find((f) => f.name === e.target.value)
                  if (fmt) {
                    setNewRow((r) => ({
                      ...r,
                      type: fmt.type as ContentType,
                      platforms: fmt.platforms.length > 0 ? fmt.platforms as Platform[] : r.platforms,
                    }))
                  }
                }}
                className="w-full bg-white text-sm text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                disabled={isCreating}
              >
                <option value="">Format...</option>
                {tableFormats.filter((f) => f.isActive).map((fmt) => (
                  <option key={fmt.id} value={fmt.name}>{fmt.name}</option>
                ))}
              </select>
            </td>
            <td className={cn(cellBase)}>
              <input
                type="text"
                value={newRow.title}
                onChange={(e) => setNewRow((r) => ({ ...r, title: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateRow() }}
                placeholder="Titolo..."
                className="w-full bg-white text-sm text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400 placeholder:text-slate-400"
                disabled={isCreating}
              />
            </td>
            <td className={cn(cellBase)}>
              {/* Compact trigger showing selected platforms */}
              <button
                ref={platformBtnRef}
                type="button"
                disabled={isCreating}
                onClick={() => setShowPlatformPicker((v) => !v)}
                className={cn(
                  'w-full flex items-center gap-1 bg-white border rounded px-2 py-1 text-left transition-colors',
                  newRow.platforms.length === 0
                    ? 'border-danger-300 text-slate-400'
                    : 'border-slate-200 text-slate-600',
                  'hover:border-primary-400 focus:outline-none focus:border-primary-400',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {newRow.platforms.length === 0 ? (
                  <span className="text-xs text-slate-400">Scegli...</span>
                ) : (
                  <span className="flex items-center gap-1">
                    {newRow.platforms.map((p) => (
                      <span
                        key={p}
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: PLATFORM_COLORS[p] ?? '#94a3b8' }}
                        title={TABLE_PLATFORM_OPTIONS.find((o) => o.value === p)?.label}
                      />
                    ))}
                    <span className="text-[10px] font-medium text-slate-500 ml-0.5">
                      {newRow.platforms.length === 1
                        ? TABLE_PLATFORM_OPTIONS.find((o) => o.value === newRow.platforms[0])?.label
                        : `${newRow.platforms.length} piattaforme`}
                    </span>
                  </span>
                )}
              </button>

              {/* Portal-based dropdown popover */}
              {showPlatformPicker && createPortal(
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setShowPlatformPicker(false)} />
                  <div
                    className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-dropdown p-2 min-w-[160px]"
                    style={(() => {
                      const rect = platformBtnRef.current?.getBoundingClientRect()
                      if (!rect) return {}
                      // Open upward: position bottom edge at button's top
                      return {
                        left: rect.left,
                        bottom: window.innerHeight - rect.top + 4,
                      }
                    })()}
                  >
                    {TABLE_PLATFORM_OPTIONS.map((o) => {
                      const isActive = newRow.platforms.includes(o.value)
                      const color = PLATFORM_COLORS[o.value] ?? '#64748B'
                      return (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() =>
                            setNewRow((r) => ({
                              ...r,
                              platforms: r.platforms.includes(o.value)
                                ? r.platforms.filter((p) => p !== o.value)
                                : [...r.platforms, o.value],
                            }))
                          }
                          className={cn(
                            'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors',
                            isActive
                              ? 'bg-slate-50 text-slate-800'
                              : 'text-slate-500 hover:bg-slate-50'
                          )}
                        >
                          <span
                            className={cn(
                              'w-3 h-3 rounded border-2 flex-shrink-0 transition-colors',
                              isActive ? 'border-transparent' : 'border-slate-300'
                            )}
                            style={isActive ? { backgroundColor: color } : undefined}
                          />
                          {o.label}
                        </button>
                      )
                    })}
                  </div>
                </>,
                document.body
              )}
            </td>
            <td className={cn(cellBase)}>
              <select
                value={newRow.type}
                onChange={(e) => setNewRow((r) => ({ ...r, type: e.target.value as ContentType }))}
                className="w-full bg-white text-xs text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                disabled={isCreating}
              >
                {TABLE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </td>
            <td className={cn(cellBase)}>
              <select
                value={newRow.status}
                onChange={(e) => setNewRow((r) => ({ ...r, status: e.target.value as ContentStatus }))}
                className="w-full bg-white text-xs text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
                disabled={isCreating}
              >
                {TABLE_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </td>
            <td className={cn(cellBase)}>
              <input
                type="text"
                value={newRow.caption}
                onChange={(e) => setNewRow((r) => ({ ...r, caption: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateRow() }}
                placeholder="Caption..."
                className="w-full bg-white text-xs text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400 placeholder:text-slate-400"
                disabled={isCreating}
              />
            </td>
            <td className={cn(cellBase)}>
              <input
                type="text"
                value={newRow.hashtags}
                onChange={(e) => setNewRow((r) => ({ ...r, hashtags: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateRow() }}
                placeholder="hashtag1, hashtag2"
                className="w-full bg-white text-xs text-slate-600 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400 placeholder:text-slate-400"
                disabled={isCreating}
              />
            </td>
            <td className={cn(cellBase)}>
              <button
                type="button"
                onClick={handleCreateRow}
                disabled={isCreating || !newRow.title.trim() || newRow.platforms.length === 0}
                className={cn(
                  'flex items-center gap-1 px-2 py-1.5 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors',
                  'disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium'
                )}
                title="Crea contenuto (Enter)"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </td>
          </tr>}
        </tbody>
      </table>
    </div>
  )
}

// ─── Format helpers ────────────────────────────────────────────────────────────

const FORMAT_TYPE_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  POST: { label: 'Post', icon: FileText, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  REEL: { label: 'Reel', icon: Film, color: 'bg-pink-50 text-pink-700 border-pink-200' },
  STORY: { label: 'Story', icon: Image, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  CAROUSEL: { label: 'Carosello', icon: Layers, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  VIDEO: { label: 'Video', icon: Video, color: 'bg-red-50 text-red-700 border-red-200' },
  ARTICLE: { label: 'Articolo', icon: BookOpen, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  INFOGRAPHIC: { label: 'Infografica', icon: BarChart3, color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
}

const FORMAT_PLATFORM_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn',
  YOUTUBE: 'YouTube',
  TWITTER: 'X / Twitter',
  PINTEREST: 'Pinterest',
}

const formatInputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

function FormatCard({ format, onEdit, onDelete, onClick }: { format: SocialFormat; onEdit: () => void; onDelete: () => void; onClick: () => void }) {
  const specs = format.specs as Record<string, unknown> | null | undefined
  const allTypes: string[] = specs?.types && Array.isArray(specs.types) ? specs.types as string[] : [format.type]
  const firstMeta = FORMAT_TYPE_META[allTypes[0] ?? 'POST'] ?? FORMAT_TYPE_META['POST']!
  const Icon = firstMeta!.icon

  return (
    <div
      className={cn(
        'bg-white rounded-2xl border border-slate-200 shadow-card flex flex-col overflow-hidden transition-all hover:shadow-card-hover cursor-pointer',
        !format.isActive && 'opacity-50'
      )}
      onClick={onClick}
    >
      <div className={cn('px-4 py-3 flex items-center gap-2 border-b', firstMeta!.color)}>
        <Icon className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {allTypes.map((t) => FORMAT_TYPE_META[t]?.label ?? t).join(' / ')}
        </span>
        {!format.isActive && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">Disattivo</span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1 gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{format.name}</h3>
        {format.description && <p className="text-xs text-slate-500 line-clamp-2">{format.description}</p>}
        <div className="flex flex-wrap gap-1 mt-1">
          {format.platforms.map((p) => (
            <span key={p} className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600">
              {FORMAT_PLATFORM_LABELS[p] ?? p}
            </span>
          ))}
        </div>
        {format.guidelines && <p className="text-[11px] text-slate-400 line-clamp-2 mt-1 italic">{format.guidelines}</p>}
      </div>
      <div className="px-4 py-2 border-t border-slate-100 flex justify-end gap-1">
        <button onClick={(e) => { e.stopPropagation(); onEdit() }} className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors" title="Modifica">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete() }} className="p-1.5 rounded-lg text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-colors" title="Elimina">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

function FormatDetailModal({ format, onClose, onEdit }: { format: SocialFormat; onClose: () => void; onEdit: () => void }) {
  const specs = format.specs as Record<string, unknown> | null | undefined
  const allTypes: string[] = specs?.types && Array.isArray(specs.types) ? specs.types as string[] : [format.type]
  const firstMeta = FORMAT_TYPE_META[allTypes[0] ?? 'POST'] ?? FORMAT_TYPE_META['POST']!
  const Icon = firstMeta!.icon

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl shadow-modal w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn('px-6 py-4 flex items-center gap-3 border-b rounded-t-3xl', firstMeta!.color)}>
          <Icon className="w-5 h-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">
            {allTypes.map((t) => FORMAT_TYPE_META[t]?.label ?? t).join(' / ')}
          </span>
          {!format.isActive && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">Disattivo</span>
          )}
          <button onClick={onClose} className="ml-auto p-1 rounded-lg hover:bg-black/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Title */}
          <h2 className="text-lg font-bold text-slate-900">{format.name}</h2>

          {/* Description */}
          {format.description && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Descrizione</h4>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{format.description}</p>
            </div>
          )}

          {/* Platforms */}
          <div>
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Piattaforme</h4>
            <div className="flex flex-wrap gap-2">
              {format.platforms.map((p) => (
                <span key={p} className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                  {FORMAT_PLATFORM_LABELS[p] ?? p}
                </span>
              ))}
            </div>
          </div>

          {/* Specs */}
          {specs && Object.keys(specs).filter(k => k !== 'types').length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Specifiche tecniche</h4>
              <div className="grid grid-cols-2 gap-3">
                {!!(specs.width && specs.height) && (
                  <div className="bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-slate-400 uppercase font-medium">Dimensioni</span>
                    <p className="text-sm font-semibold text-slate-800">{String(specs.width)} × {String(specs.height)} px</p>
                  </div>
                )}
                {!!specs.aspectRatio && (
                  <div className="bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-slate-400 uppercase font-medium">Aspect Ratio</span>
                    <p className="text-sm font-semibold text-slate-800">{String(specs.aspectRatio)}</p>
                  </div>
                )}
                {!!specs.duration && (
                  <div className="bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-slate-400 uppercase font-medium">Durata</span>
                    <p className="text-sm font-semibold text-slate-800">{String(specs.duration)}</p>
                  </div>
                )}
                {!!specs.maxChars && (
                  <div className="bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-slate-400 uppercase font-medium">Max caratteri</span>
                    <p className="text-sm font-semibold text-slate-800">{String(specs.maxChars)}</p>
                  </div>
                )}
                {!!specs.slides && (
                  <div className="bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-slate-400 uppercase font-medium">Slide</span>
                    <p className="text-sm font-semibold text-slate-800">{String(specs.slides)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Guidelines */}
          {format.guidelines && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Linee guida editoriali</h4>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{format.guidelines}</p>
            </div>
          )}

          {/* Example URL */}
          {format.exampleUrl && (
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Esempio di riferimento</h4>
              <a
                href={format.exampleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {format.exampleUrl}
              </a>
            </div>
          )}

          {/* Meta info */}
          <div className="flex items-center gap-4 pt-2 border-t border-slate-100 text-[11px] text-slate-400">
            {format.createdBy && (
              <span>Creato da {format.createdBy.firstName} {format.createdBy.lastName}</span>
            )}
            <span>Creato il {new Date(format.createdAt).toLocaleDateString('it-IT')}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Chiudi
          </button>
          <button
            onClick={() => { onClose(); onEdit() }}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" />
            Modifica
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function FormatFormModal({ format, projectId, onClose, onSuccess }: { format: SocialFormat | null; projectId: string; onClose: () => void; onSuccess: () => void }) {
  const [name, setName] = useState(format?.name ?? '')
  const [description, setDescription] = useState(format?.description ?? '')
  const [types, setTypes] = useState<string[]>(() => {
    const s = format?.specs as Record<string, unknown> | null | undefined
    if (s?.types && Array.isArray(s.types)) return s.types as string[]
    if (format?.type) return [format.type]
    return []
  })
  const [platforms, setPlatforms] = useState<string[]>(format?.platforms ?? [])
  const [guidelines, setGuidelines] = useState(format?.guidelines ?? '')
  const [exampleUrl, setExampleUrl] = useState(format?.exampleUrl ?? '')
  const [isActive, setIsActive] = useState(format?.isActive ?? true)
  const [specWidth, setSpecWidth] = useState(format?.specs?.width?.toString() ?? '')
  const [specHeight, setSpecHeight] = useState(format?.specs?.height?.toString() ?? '')
  const [specAspectRatio, setSpecAspectRatio] = useState(format?.specs?.aspectRatio ?? '')
  const [specDuration, setSpecDuration] = useState(format?.specs?.duration ?? '')
  const [specMaxChars, setSpecMaxChars] = useState(format?.specs?.maxChars?.toString() ?? '')
  const [specSlides, setSpecSlides] = useState(format?.specs?.slides?.toString() ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const toggleType = (t: string) => setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t])
  const togglePlatform = (p: string) => setPlatforms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Nome obbligatorio'); return }
    if (types.length === 0) { setError('Seleziona almeno un tipo contenuto'); return }
    if (platforms.length === 0) { setError('Seleziona almeno una piattaforma'); return }
    setError('')
    setSaving(true)
    const specs: Record<string, unknown> = {}
    if (specWidth) specs.width = parseInt(specWidth)
    if (specHeight) specs.height = parseInt(specHeight)
    if (specAspectRatio) specs.aspectRatio = specAspectRatio
    if (specDuration) specs.duration = specDuration
    if (specMaxChars) specs.maxChars = parseInt(specMaxChars)
    if (specSlides) specs.slides = parseInt(specSlides)
    specs.types = types
    const payload = { name: name.trim(), description: description.trim() || null, type: types[0], platforms, specs, guidelines: guidelines.trim() || null, exampleUrl: exampleUrl.trim() || null, isActive }
    try {
      if (format) await socialApi.updateFormat(format.id, payload)
      else await socialApi.createFormat({ ...payload, projectId })
      onSuccess()
    } catch (e) { setError(getApiErrorMessage(e)) } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-modal w-full max-w-2xl z-10 my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{format ? 'Modifica Format' : 'Nuovo Format'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {error && <div className="px-3 py-2 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nome Format *</label>
            <input className={formatInputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Post Singolo, Carosello 5 slide, Reel 30s..." />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Tipo Contenuto *</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(FORMAT_TYPE_META).map(([key, m]) => (
                <button key={key} type="button" onClick={() => toggleType(key)} className={cn('px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors', types.includes(key) ? 'bg-warning-500 text-white border-warning-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Piattaforme *</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(FORMAT_PLATFORM_LABELS).map(([key, label]) => (
                <button key={key} type="button" onClick={() => togglePlatform(key)} className={cn('px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors', platforms.includes(key) ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Linee Guida Editoriali</label>
            <textarea className={cn(formatInputClass, 'resize-none')} rows={3} value={guidelines} onChange={(e) => setGuidelines(e.target.value)} placeholder="Tono di voce, struttura del contenuto, CTA, regole grafiche..." />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">Annulla</button>
          <button onClick={handleSubmit} disabled={saving} className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
            {saving ? 'Salvataggio...' : format ? 'Salva Modifiche' : 'Crea Format'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Day Panel (visible in calendar view) ────────────────────────────────────

const DAYS_IT_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato']
const MONTHS_IT_FULL = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function PEDDayPanel({
  selectedDay,
  allContents,
  onContentClick,
  onAddForDay,
  onChangeDay,
}: {
  selectedDay: Date
  allContents: Content[]
  onContentClick: (c: Content) => void
  onAddForDay: (date: Date) => void
  onChangeDay: (delta: number) => void
}) {
  const dayStr = toLocalDateStr(selectedDay)
  const todayStr = toLocalDateStr(new Date())
  const isToday = dayStr === todayStr

  const contentsByDate = allContents.reduce<Record<string, Content[]>>((acc, c) => {
    if (!c.scheduledAt) return acc
    const key = toLocalDateStr(new Date(c.scheduledAt))
    if (!acc[key]) acc[key] = []
    acc[key].push(c)
    return acc
  }, {})

  const dayContents = contentsByDate[dayStr] ?? []

  // Next 14 days that have content
  const upcoming: { dateStr: string; label: string; items: Content[] }[] = []
  for (let i = 1; i <= 14; i++) {
    const d = new Date(selectedDay)
    d.setDate(d.getDate() + i)
    const key = toLocalDateStr(d)
    const items = contentsByDate[key]
    if (items && items.length > 0) {
      upcoming.push({
        dateStr: key,
        label: `${DAYS_IT_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS_IT_FULL[d.getMonth()]}`,
        items,
      })
    }
  }

  return (
    <div className="flex flex-col bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden h-full">
      {/* Header */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-slate-200">
        <div className="min-w-0">
          <h3 className="text-sm font-bold tracking-tight text-slate-800">
            {isToday ? 'Oggi' : DAYS_IT_FULL[selectedDay.getDay()]}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {selectedDay.getDate()} {MONTHS_IT_FULL[selectedDay.getMonth()]} {selectedDay.getFullYear()}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onChangeDay(-1)}
            className="w-7 h-7 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-all"
            title="Giorno precedente"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
          </button>
          <button
            onClick={() => onChangeDay(1)}
            className="w-7 h-7 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-all"
            title="Giorno successivo"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4">
        {/* Contenuti del giorno */}
        <div className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Contenuti del giorno</p>
            <button
              onClick={() => onAddForDay(selectedDay)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors active:scale-95"
              title="Aggiungi contenuto in questo giorno"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          {dayContents.length === 0 ? (
            <div className="py-5 text-center">
              <CalendarDays className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Nessun contenuto pianificato</p>
              <button
                onClick={() => onAddForDay(selectedDay)}
                className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                + Aggiungi contenuto
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {dayContents.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onContentClick(c)}
                  className="w-full flex items-start gap-2.5 p-2.5 rounded-xl border border-slate-100 hover:border-primary-200 hover:bg-primary-50/40 transition-colors text-left group"
                >
                  <span className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1', STATUS_DOT_COLOR[c.status])} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate group-hover:text-primary-700">
                      {c.title || 'Senza titolo'}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {(c.platforms ?? []).slice(0, 2).map((p) => (
                        <span
                          key={p}
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: PLATFORM_COLORS[p] ?? '#94a3b8' }}
                        />
                      ))}
                      <span className="text-[10px] text-slate-400 font-medium ml-0.5">
                        {TABLE_STATUS_OPTIONS.find((o) => o.value === STATUS_TO_STEP_STATUS[c.status])?.label ?? c.status}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Prossimi contenuti */}
        {upcoming.length > 0 && (
          <div className="pt-5 pb-4">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em] mb-2.5">Prossimi contenuti</p>
            <div className="space-y-4">
              {upcoming.slice(0, 5).map(({ dateStr, label, items }) => (
                <div key={dateStr}>
                  <p className="text-[11px] font-semibold text-slate-500 mb-1.5">{label}</p>
                  <div className="space-y-1">
                    {items.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => onContentClick(c)}
                        className="w-full flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT_COLOR[c.status])} />
                        <span className="text-xs text-slate-600 truncate group-hover:text-primary-600 flex-1">{c.title || 'Senza titolo'}</span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0 ml-1">
                          {(c.platforms ?? []).slice(0, 1).map((p) => (
                            <span key={p} className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: PLATFORM_COLORS[p] ?? '#94a3b8' }} />
                          ))}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main PEDPage ─────────────────────────────────────────────────────────────

export function PEDPage() {
  const queryClient = useQueryClient()
  const today = new Date()
  const pf = useProjectFilter()
  const authUser = useAuthStore((state) => state.user)
  const userLevel = authUser?.role?.level ?? 4
  // `ped:delete` = L1-L3 (contenuti, piani e format); `canDeletePed` = flag mirato sul ruolo
  // (Social Media Manager) che apre la sola eliminazione dei contenuti a un operativo L4.
  // Il perimetro effettivo (solo i contenuti dei propri progetti, mai i pubblicati) lo
  // applica il backend.
  const canDeleteContent =
    authUser?.isSuperAdmin ||
    authUser?.role?.canDeletePed ||
    authUser?.role?.permissions?.ped?.includes('delete') ||
    false

  // I guest hanno una vista PED dedicata (sola lettura + approva/richiedi modifiche)
  if (authUser?.isGuest) {
    return <GuestPedPage />
  }

  const [pageMode, setPageMode] = useState<'contenuti' | 'format'>('contenuti')
  const [viewMode, setViewMode] = useState<'calendar' | 'kanban' | 'table'>('calendar')
  const [selectedProjectId, setSelectedProjectId] = useState<string>(pf.projectId ?? '')
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth()) // 0-based
  const [selectedDay, setSelectedDay] = useState<Date>(today)
  const [filterPlatform, setFilterPlatform] = useState<Platform | ''>('')
  const [filterStatus, setFilterStatus] = useState<ContentStatus | ''>('')
  const [pedSearchParams, setPedSearchParams] = useSearchParams()
  const deepLinkContentId = pedSearchParams.get('contentId') || ''
  const [selectedContent, setSelectedContent] = useState<Content | null>(null)
  const [showContentForm, setShowContentForm] = useState(false)
  const [contentFormInitial, setContentFormInitial] = useState<Partial<ContentFormData> | null>(null)
  const [formatModal, setFormatModal] = useState<SocialFormat | 'new' | null>(null)
  const [formatDetail, setFormatDetail] = useState<SocialFormat | null>(null)
  const [formatToast, setFormatToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [exportingPdf, setExportingPdf] = useState(false)

  // formats query is below after effectiveProjectId

  const showFormatToast = (message: string, type: 'success' | 'error') => {
    setFormatToast({ message, type })
    setTimeout(() => setFormatToast(null), 3500)
  }

  // Sync: if URL has ?projectId, pre-select that project
  useEffect(() => {
    if (pf.projectId) setSelectedProjectId(pf.projectId)
  }, [pf.projectId])

  // Fetch projects list (tutti i progetti non chiusi)
  const { data: projectsRaw, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects-ped'],
    queryFn: () => projectsApi.list({ limit: 100 }),
    staleTime: 60_000,
  })

  const projects: Array<{ id: string; name: string; status?: string; client?: { id: string; name: string } }> = (() => {
    const raw = projectsRaw as any
    const items: any[] = raw?.items ?? raw?.data?.items ?? raw?.data ?? []
    if (!Array.isArray(items)) return []
    return items.filter((p) => p.status !== 'COMPLETED' && p.status !== 'CANCELLED')
  })()

  // Auto-select first project
  const effectiveProjectId = selectedProjectId || projects[0]?.id || ''
  const selectedProject = projects.find((p) => p.id === effectiveProjectId)

  // Fetch formats for selected project
  const { data: formatsData, refetch: refetchFormats } = useQuery({
    queryKey: ['social-formats', effectiveProjectId],
    queryFn: async () => { const res = await socialApi.listFormats(effectiveProjectId); return res.data },
    enabled: !!effectiveProjectId,
  })
  const formats = (formatsData?.data ?? formatsData ?? []) as SocialFormat[]

  // Fetch contents for selected project
  const { data: contentsData, isLoading: contentsLoading } = useQuery({
    queryKey: ['ped-contents', effectiveProjectId],
    queryFn: () => pedApi.getProjectContents(effectiveProjectId),
    enabled: !!effectiveProjectId,
    staleTime: 30_000,
  })

  // Fetch plan info (for share token)
  const { data: planData } = useQuery({
    queryKey: ['ped-plan', effectiveProjectId],
    queryFn: () => pedApi.listPlans({ projectId: effectiveProjectId }),
    enabled: !!effectiveProjectId,
    staleTime: 60_000,
  })
  const currentPlanId = (() => {
    const raw = planData as any
    const plans = raw?.data ?? raw?.items ?? raw ?? []
    return Array.isArray(plans) && plans.length > 0 ? plans[0]?.id : null
  })()

  // Share state
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [shareLoading, setShareLoading] = useState(false)
  const [shareCopied, setShareCopied] = useState(false)

  const loadShareToken = async () => {
    if (!currentPlanId) return
    setShareLoading(true)
    try {
      const res = await pedApi.getShareLink(currentPlanId)
      const data = res?.data ?? res
      setShareToken(data?.token ?? null)
    } catch {
      setShareToken(null)
    } finally {
      setShareLoading(false)
    }
  }

  const handleGenerateShareLink = async () => {
    if (!currentPlanId) return
    setShareLoading(true)
    try {
      const res = await pedApi.generateShareLink(currentPlanId)
      const data = res?.data ?? res
      setShareToken(data?.token ?? null)
    } catch {
      /* error */
    } finally {
      setShareLoading(false)
    }
  }

  const handleRevokeShareLink = async () => {
    if (!currentPlanId) return
    setShareLoading(true)
    try {
      await pedApi.revokeShareLink(currentPlanId)
      setShareToken(null)
    } catch {
      /* error */
    } finally {
      setShareLoading(false)
    }
  }

  const shareUrl = shareToken ? `${window.location.origin}/shared/ped/${shareToken}` : null

  const moveContentMutation = useMutation({
    mutationFn: ({ contentId, status }: { contentId: string; status: ContentStatus }) =>
      pedApi.updateContent(contentId, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', effectiveProjectId] })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', effectiveProjectId] })
    },
  })

  const allContents: Content[] = (() => {
    const raw = contentsData as any
    return raw?.data ?? raw?.items ?? raw ?? []
  })()

  // Deep link: auto-select content from ?contentId=xxx
  useEffect(() => {
    if (!deepLinkContentId || !allContents.length) return
    const found = allContents.find((c) => c.id === deepLinkContentId)
    if (found && !selectedContent) {
      setSelectedContent(found)
      // Remove contentId from URL
      pedSearchParams.delete('contentId')
      setPedSearchParams(pedSearchParams, { replace: true })
    }
  }, [deepLinkContentId, allContents])

  // Apply platform + status filter
  const contents = allContents.filter((c) => {
    // platform filter removed
    if (filterStatus && c.status !== filterStatus) return false
    return true
  })

  // Count contents needing revision
  const changesRequestedCount = allContents.filter((c) => c.status === 'CHANGES_REQUESTED').length

  // Stats
  const stats = {
    idee: allContents.filter((c) => c.status === 'IDEA' || c.status === 'TODO').length,
    inProduzione: allContents.filter((c) => c.status === 'IN_PRODUCTION' || c.status === 'READY').length,
    inReview: allContents.filter((c) => c.status === 'IN_REVIEW' || c.status === 'CHANGES_REQUESTED').length,
    approvati: allContents.filter((c) => c.status === 'APPROVED' || c.status === 'SCHEDULED').length,
    pubblicati: allContents.filter((c) => c.status === 'PUBLISHED').length,
  }

  function handlePrevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear((y) => y - 1)
    } else {
      setCurrentMonth((m) => m - 1)
    }
  }

  function handleNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear((y) => y + 1)
    } else {
      setCurrentMonth((m) => m + 1)
    }
  }

  function handleContentMove(contentId: string, newStatus: ContentStatus) {
    moveContentMutation.mutate({ contentId, status: newStatus })
  }

  function handleStatusChange(contentId: string, newStatus: ContentStatus) {
    handleContentMove(contentId, newStatus)
    if (selectedContent?.id === contentId) {
      setSelectedContent((prev) => prev ? { ...prev, status: newStatus } : null)
    }
  }

  async function handleContentUpdate(contentId: string, data: Record<string, unknown>) {
    try {
      await pedApi.updateContent(contentId, data)
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', effectiveProjectId] })
      // Update local selectedContent so the modal reflects changes immediately
      setSelectedContent((prev) => prev?.id === contentId ? { ...prev, ...data } as Content : prev)
    } catch {
      // silent — invalidate anyway to re-sync
      void queryClient.invalidateQueries({ queryKey: ['ped-contents', effectiveProjectId] })
    }
  }

  function handleDayClick(date: Date) {
    setSelectedDay(date)
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    setContentFormInitial({ scheduledAt: `${yyyy}-${mm}-${dd}T10:00` })
    setShowContentForm(true)
  }

  function handleDaySelect(date: Date) {
    setSelectedDay(date)
    // When switching to a different month, navigate there
    if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) {
      setCurrentMonth(date.getMonth())
      setCurrentYear(date.getFullYear())
    }
  }

  function handleAddForDay(date: Date) {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    setContentFormInitial({ scheduledAt: `${yyyy}-${mm}-${dd}T10:00` })
    setShowContentForm(true)
  }

  function handleKanbanQuickAdd(status: ContentStatus) {
    setContentFormInitial({ status })
    setShowContentForm(true)
  }

  function handleOpenNewContent() {
    setContentFormInitial(null)
    setShowContentForm(true)
  }

  function handleCloseContentForm() {
    setShowContentForm(false)
    setContentFormInitial(null)
  }

  async function handleExportPdf() {
    if (!effectiveProjectId) return
    setExportingPdf(true)
    try {
      const params = viewMode === 'calendar'
        ? { year: currentYear, month: currentMonth }
        : undefined
      const response = await pedApi.exportPdf(effectiveProjectId, params)
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Get filename from Content-Disposition header
      const cd = response.headers['content-disposition'] ?? ''
      const match = cd.match(/filename="([^"]+)"/)
      a.download = match?.[1] ?? `ped-${effectiveProjectId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      window.alert('Errore durante l\'esportazione del PDF: ' + getApiErrorMessage(err))
    }
    setExportingPdf(false)
  }

  const isLoading = projectsLoading || (!!effectiveProjectId && contentsLoading)

  return (
    <div className="space-y-5">
      {/* Project filter banner */}
      <ProjectFilterBanner
        projectId={pf.projectId}
        projectName={pf.projectName}
        clientName={pf.clientName}
        onClear={pf.clearFilter}
      />

      {/* Toast */}
      {formatToast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-dropdown text-sm font-medium flex items-center gap-2 animate-fade-in',
          formatToast.type === 'success' ? 'bg-success-50 text-success-700 border border-success-200' : 'bg-danger-50 text-danger-700 border border-danger-200'
        )}>
          {formatToast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-slate-900">Piano Editoriale</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Gestione contenuti e calendario editoriale
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Page mode toggle */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-md overflow-hidden bg-white">
            <button
              onClick={() => setPageMode('contenuti')}
              className={cn('flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-colors', pageMode === 'contenuti' ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
            >
              <Calendar className="w-3.5 h-3.5" />
              Contenuti
            </button>
            <button
              onClick={() => setPageMode('format')}
              className={cn('flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-colors', pageMode === 'format' ? 'bg-warning-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Format
            </button>
          </div>

          {/* Project selector — solo in modalita contenuti e senza filtro progetto */}
          {pageMode === 'contenuti' && !pf.projectId && (
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 min-w-0 w-full sm:w-auto">
              <FolderOpen className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={effectiveProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="text-sm font-medium text-slate-700 bg-transparent border-0 focus:outline-none cursor-pointer pr-6 truncate min-w-0 flex-1"
              >
                {projects.length === 0 && <option value="">Nessun progetto</option>}
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.client ? ` — ${p.client.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {pageMode === 'contenuti' && (
            <button
              onClick={handleExportPdf}
              disabled={!effectiveProjectId || exportingPdf}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg',
                'hover:bg-slate-50 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              title="Esporta piano editoriale in PDF"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">{exportingPdf ? 'Esportazione...' : 'Esporta PDF'}</span>
            </button>
          )}

          {pageMode === 'contenuti' && userLevel <= 3 && (
            <button
              onClick={() => { setShowShareModal(true); loadShareToken() }}
              disabled={!effectiveProjectId}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg',
                'hover:bg-slate-50 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Condividi</span>
            </button>
          )}

          {pageMode === 'contenuti' && userLevel < 4 && (
            <button
              onClick={handleOpenNewContent}
              disabled={!effectiveProjectId}
              className={cn(
                'flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg',
                'hover:bg-primary-700 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nuovo Contenuto</span>
              <span className="sm:hidden">Nuovo</span>
            </button>
          )}

          {pageMode === 'format' && (
            <button
              onClick={() => setFormatModal('new')}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nuovo Format</span>
              <span className="sm:hidden">Nuovo</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── FORMAT VIEW ─── */}
      {pageMode === 'format' && (
        <div>
          {formats.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-200 shadow-card">
              <LayoutGrid className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-900">Nessun format creato</p>
              <p className="text-xs text-slate-500 mt-1">Crea il primo format per organizzare i contenuti del piano editoriale</p>
              {userLevel < 4 && (
                <button
                  onClick={() => setFormatModal('new')}
                  className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Crea Format
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {formats.map((fmt) => (
                <FormatCard
                  key={fmt.id}
                  format={fmt}
                  onClick={() => setFormatDetail(fmt)}
                  onEdit={() => setFormatModal(fmt)}
                  onDelete={async () => {
                    if (!confirm(`Eliminare il format "${fmt.name}"?`)) return
                    try {
                      await socialApi.deleteFormat(fmt.id)
                      refetchFormats()
                      showFormatToast('Format eliminato', 'success')
                    } catch (e) {
                      showFormatToast(getApiErrorMessage(e), 'error')
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Format Modal */}
      {formatModal && (
        <FormatFormModal
          format={formatModal === 'new' ? null : formatModal}
          projectId={effectiveProjectId}
          onClose={() => setFormatModal(null)}
          onSuccess={() => {
            refetchFormats()
            showFormatToast(formatModal === 'new' ? 'Format creato' : 'Format aggiornato', 'success')
            setFormatModal(null)
          }}
        />
      )}

      {/* Format Detail Modal */}
      {formatDetail && (
        <FormatDetailModal
          format={formatDetail}
          onClose={() => setFormatDetail(null)}
          onEdit={() => { setFormatDetail(null); setFormatModal(formatDetail) }}
        />
      )}

      {/* ─── CONTENUTI VIEW ─── */}
      {pageMode === 'contenuti' && (
        <>

      {/* Empty state: no projects */}
      {!projectsLoading && projects.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-card">
          <AlertTriangle className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">Nessun progetto attivo</p>
          <p className="text-xs text-slate-400 mt-1">Crea un progetto per iniziare ad aggiungere contenuti al piano editoriale.</p>
        </div>
      )}

      {projects.length > 0 && effectiveProjectId && (
        <>
          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {([
              { label: 'Idee & Brief', count: stats.idee, icon: Lightbulb, tone: 'neutral' as const },
              { label: 'In Produzione', count: stats.inProduzione, icon: Hammer, tone: 'warning' as const },
              { label: 'In Review', count: stats.inReview, icon: Eye, tone: 'warning' as const },
              { label: 'Approvati', count: stats.approvati, icon: CheckCircle2, tone: 'primary' as const },
              { label: 'Pubblicati', count: stats.pubblicati, icon: Send, tone: 'success' as const },
            ]).map((stat) => (
              <StatCard
                key={stat.label}
                label={stat.label}
                value={stat.count}
                icon={stat.icon}
                tone={stat.tone}
              />
            ))}
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Month navigation (only for calendar view) */}
            {viewMode === 'calendar' && (
              <div className="flex items-center gap-1 border border-slate-200 rounded-md bg-white">
                <button
                  onClick={handlePrevMonth}
                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors rounded-l-md"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-2 sm:px-3 text-xs sm:text-sm font-semibold text-slate-700 min-w-[100px] sm:min-w-[120px] text-center">
                  {MONTH_LABELS[currentMonth]} {currentYear}
                </span>
                <button
                  onClick={handleNextMonth}
                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors rounded-r-md"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* View toggle — visibile per primo su mobile */}
            <div className="flex sm:hidden items-center gap-1 ml-auto border border-slate-200 rounded-md overflow-hidden bg-white order-first sm:order-none">
              <button
                onClick={() => setViewMode('calendar')}
                className={cn('p-1.5 transition-colors', viewMode === 'calendar' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50')}
                title="Vista Calendario"
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={cn('p-1.5 transition-colors', viewMode === 'kanban' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50')}
                title="Vista Kanban"
              >
                <Columns className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={cn('p-1.5 transition-colors', viewMode === 'table' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50')}
                title="Vista Tabella"
              >
                <TableProperties className="w-4 h-4" />
              </button>
            </div>

            {/* Status filters (hidden in table view) */}
            <div className={cn('flex items-center gap-1.5 flex-wrap w-full sm:w-auto', viewMode === 'table' && 'hidden')}>
              {[
                { value: '' as const, label: 'Tutti', color: '' },
                { value: 'CHANGES_REQUESTED' as const, label: 'Da modificare', color: 'bg-danger-500' },
                { value: 'IN_REVIEW' as const, label: 'In review', color: 'bg-warning-500' },
                { value: 'APPROVED' as const, label: 'Approvati', color: 'bg-success-500' },
                { value: 'IN_PRODUCTION' as const, label: 'Produzione', color: 'bg-warning-400' },
                { value: 'PUBLISHED' as const, label: 'Pubblicati', color: 'bg-success-500' },
              ].map(({ value, label, color }) => {
                const isActive = filterStatus === value
                return (
                  <button
                    key={value || 'all-status'}
                    onClick={() => setFilterStatus(isActive && value !== '' ? '' : value as ContentStatus | '')}
                    className={cn(
                      'px-2.5 py-1 text-xs font-medium rounded-full border transition-colors flex items-center gap-1.5',
                      isActive
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    )}
                  >
                    {color && <span className={cn('w-2 h-2 rounded-full', color)} />}
                    {label}
                    {value === 'CHANGES_REQUESTED' && changesRequestedCount > 0 && !isActive && (
                      <span className="ml-0.5 px-1.5 py-0 text-[10px] font-bold bg-danger-100 text-danger-700 rounded-full tabular-nums">{changesRequestedCount}</span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* View toggle (desktop) */}
            <div className="hidden sm:flex items-center gap-1 ml-auto border border-slate-200 rounded-md overflow-hidden bg-white">
              <button
                onClick={() => setViewMode('calendar')}
                className={cn(
                  'p-1.5 transition-colors',
                  viewMode === 'calendar' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50'
                )}
                title="Vista Calendario"
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={cn(
                  'p-1.5 transition-colors',
                  viewMode === 'kanban' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50'
                )}
                title="Vista Kanban"
              >
                <Columns className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={cn(
                  'p-1.5 transition-colors',
                  viewMode === 'table' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50'
                )}
                title="Vista Tabella"
              >
                <TableProperties className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Changes Requested Alert Banner */}
          {changesRequestedCount > 0 && !filterStatus && (
            <button
              onClick={() => {
                setFilterStatus('CHANGES_REQUESTED')
                // Mostra anche il contenuto che richiede modifiche: "Cinematic Roma" - lo trova nel calendario
                const changedContent = allContents.find(c => c.status === 'CHANGES_REQUESTED')
                if (changedContent) {
                  // Apri il dettaglio del contenuto
                  setSelectedContent(changedContent)
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-danger-50 border border-danger-200 rounded-2xl text-left hover:bg-danger-100 transition-colors"
            >
              <span className="flex-shrink-0 w-8 h-8 bg-danger-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-danger-600" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-danger-700">
                  <span className="tabular-nums">{changesRequestedCount}</span> {changesRequestedCount === 1 ? 'contenuto richiede' : 'contenuti richiedono'} modifiche
                </p>
                <p className="text-xs text-danger-600">Clicca per filtrare i contenuti da rivedere</p>
              </div>
              <ChevronRight className="w-4 h-4 text-danger-400 flex-shrink-0" />
            </button>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="h-64 bg-white rounded-2xl border border-slate-200 animate-pulse" />
          ) : viewMode === 'calendar' ? (
            <div className="flex gap-4 items-start">
              <div className="flex-1 min-w-0">
                <PEDCalendar
                  contents={contents}
                  year={currentYear}
                  month={currentMonth}
                  onContentClick={(c) => setSelectedContent(c)}
                  onDayClick={handleDayClick}
                  selectedDayStr={toLocalDateStr(selectedDay)}
                  onDaySelect={handleDaySelect}
                />
              </div>
              <aside className="hidden xl:flex xl:flex-col w-[320px] flex-shrink-0" style={{ minHeight: 500 }}>
                {selectedContent ? (
                  <ContentDetailModal
                    content={selectedContent}
                    variant="panel"
                    onClose={() => setSelectedContent(null)}
                    onStatusChange={handleStatusChange}
                    onUpdate={handleContentUpdate}
                  />
                ) : (
                  <PEDDayPanel
                    selectedDay={selectedDay}
                    allContents={allContents}
                    onContentClick={(c) => setSelectedContent(c)}
                    onAddForDay={handleAddForDay}
                    onChangeDay={(delta) => {
                      setSelectedDay((d) => {
                        const n = new Date(d)
                        n.setDate(n.getDate() + delta)
                        if (n.getMonth() !== currentMonth || n.getFullYear() !== currentYear) {
                          setCurrentMonth(n.getMonth())
                          setCurrentYear(n.getFullYear())
                        }
                        return n
                      })
                    }}
                  />
                )}
              </aside>
            </div>
          ) : viewMode === 'table' ? (
            <PEDTableView
              contents={contents}
              projectId={effectiveProjectId}
              onContentClick={(c) => setSelectedContent(c)}
              userLevel={userLevel}
              canDelete={canDeleteContent}
            />
          ) : (
            <PEDKanban
              contents={contents}
              onContentClick={(c) => setSelectedContent(c)}
              onContentMove={handleContentMove}
              onQuickAdd={handleKanbanQuickAdd}
            />
          )}
        </>
      )}

      </>
      )}

      {/* Content detail modal — solo per kanban/table (in calendario il pannello laterale fa da dettaglio) */}
      {selectedContent && viewMode !== 'calendar' && (
        <ContentDetailModal
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
          onStatusChange={handleStatusChange}
          onUpdate={handleContentUpdate}
        />
      )}

      {/* New content form modal */}
      {showContentForm && effectiveProjectId && (
        <ContentFormModal
          planId={effectiveProjectId}
          onClose={handleCloseContentForm}
          onSuccess={handleCloseContentForm}
          {...(contentFormInitial ? { initialData: contentFormInitial } : {})}
        />
      )}

      {/* Share modal */}
      {showShareModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowShareModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
            <div className="bg-white rounded-3xl shadow-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary-600" />
                  <h3 className="text-base font-semibold text-slate-900">Condividi PED</h3>
                </div>
                <button onClick={() => setShowShareModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-5 py-5 space-y-4">
                <p className="text-sm text-slate-600">
                  Genera un link per condividere questo piano editoriale. Chi ha il link puo visualizzare i contenuti, approvare e richiedere modifiche.
                </p>

                {shareLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : shareUrl ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                        <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700 truncate">{shareUrl}</span>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shareUrl)
                          setShareCopied(true)
                          setTimeout(() => setShareCopied(false), 2000)
                        }}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors flex-shrink-0',
                          shareCopied
                            ? 'bg-success-50 text-success-700 border border-success-200'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        )}
                      >
                        {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {shareCopied ? 'Copiato' : 'Copia'}
                      </button>
                    </div>
                    <button
                      onClick={handleRevokeShareLink}
                      className="flex items-center gap-2 text-sm text-danger-600 hover:text-danger-700 transition-colors"
                    >
                      <Unlink className="w-4 h-4" />
                      Revoca link
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleGenerateShareLink}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Link2 className="w-4 h-4" />
                    Genera link di condivisione
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
